import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

// Consistent color palette for cost types (matches Financial Reports convention)
const COST_TYPE_COLORS = {
  'Labor':         '#3b82f6',
  'Materials':     '#10b981',
  'Equipment':     '#f59e0b',
  'Subcontractor': '#8b5cf6',
  'Other':         '#ec4899',
  'Uncategorized': '#94a3b8',
};

// Fallback palette for custom cost types
const FALLBACK_COLORS = ['#0ea5e9', '#f97316', '#14b8a6', '#ef4444', '#6366f1', '#84cc16', '#e879f9', '#a3e635'];

function getColor(name, idx) {
  return COST_TYPE_COLORS[name] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
}

function formatDollars(val) {
  if (val >= 1000) return `$${(val / 1000).toFixed(val >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(val)}`;
}

/**
 * Compact stacked horizontal bar showing budget composition by cost type.
 * @param {Array} breakdown - [{name, total, percent}, ...]
 * @param {'card'|'row'} variant - 'card' for kanban, 'row' for table
 */
export function CostBreakdownBar({ breakdown, variant = 'card' }) {
  if (!breakdown || breakdown.length === 0) {
    return (
      <div className={`text-muted-foreground/40 ${variant === 'card' ? 'text-[10px] mt-1.5' : 'text-[11px]'}`}>
        No budget breakdown
      </div>
    );
  }

  const barHeight = variant === 'card' ? 'h-1.5' : 'h-2';

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`w-full ${variant === 'card' ? 'mt-1.5' : ''}`} data-testid="cost-breakdown-bar">
            {/* Stacked bar */}
            <div className={`w-full ${barHeight} rounded-full overflow-hidden flex bg-muted/30`}>
              {breakdown.map((item, idx) => (
                <div
                  key={item.name}
                  className="h-full transition-all first:rounded-l-full last:rounded-r-full"
                  style={{
                    width: `${Math.max(item.percent, 2)}%`,
                    backgroundColor: getColor(item.name, idx),
                  }}
                />
              ))}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[240px] p-2.5 !bg-white dark:!bg-zinc-900 !text-zinc-900 dark:!text-zinc-100 border border-zinc-200 dark:border-zinc-700 shadow-lg" data-testid="cost-breakdown-tooltip">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold mb-1.5">Budget by Cost Type</p>
            {breakdown.map((item, idx) => (
              <div key={item.name} className="flex items-center justify-between gap-3 text-[11px]">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: getColor(item.name, idx) }} />
                  <span className="truncate">{item.name}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="tabular-nums font-medium">{formatDollars(item.total)}</span>
                  <span className="text-[10px] text-zinc-500 dark:text-zinc-400">({item.percent}%)</span>
                </div>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
